import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import ThemeToggle from '../../../components/ThemeToggle';
import { useAuth } from '../../../contexts/AuthContext';
import { User, ChevronDown, Menu, X, Award } from 'lucide-react';

export default function NavigationHeader() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigationRef = useRef(null);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fixed click-outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navigationRef?.current && !navigationRef?.current?.contains(event?.target)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navigationLinks = [
    {
      id: 1,
      name: "Explore",
      hasDropdown: true,
      public: true,
      dropdownItems: [
        { name: "Interactive Map", path: "/interactive-conservation-map", public: true },
        { name: "Site Showcase", path: "/site-showcase-gallery", public: true },
        { name: "Biodiversity Status", path: "/biodiversity-status-center", public: true },
        { name: "Marine Turtle Conservation", path: "/marine-turtle-conservation", public: true }
      ]
    },
    {
      id: 2,
      name: "Visit",
      hasDropdown: true,
      public: true,
      dropdownItems: [
        { name: "Plan Your Trip", path: "/visitor-planning-hub", public: true },
        { name: "Visitor Analytics", path: "/visitor-analytics-dashboard", requiresAuth: true },
        { name: "Emergency Alerts", path: "/emergency-alert-system", public: true }
      ]
    },
    {
      id: 3,
      name: "Research",
      hasDropdown: true,
      requiresAuth: true,
      dropdownItems: [
        { name: "Data Portal", path: "/research-data-portal", requiresAuth: true },
        { name: "Command Center", path: "/command-center-dashboard", requiresAuth: true }
      ]
    },
    {
      id: 4,
      name: "Community",
      path: "/community-engagement-platform",
      hasDropdown: false,
      public: true
    }
  ];

  // Filter navigation links based on authentication status
  const getFilteredNavigationLinks = () => {
    return navigationLinks
      ?.filter(link => {
        // Hide if link requires auth and user is not authenticated
        if (link?.requiresAuth && !user) {
          return false;
        }
        
        // Show if link is explicitly public
        if (link?.public) {
          return true;
        }
        
        // Show if user is authenticated
        return !!user;
      })
      ?.map(link => {
        // Filter dropdown items based on authentication
        if (link?.hasDropdown && link?.dropdownItems) {
          const filteredItems = link?.dropdownItems?.filter(item => {
            // Show if item is public
            if (item?.public) {
              return true;
            }
            // Show if item requires auth AND user is authenticated
            if (item?.requiresAuth) {
              return !!user;
            }
            // Default: hide if not explicitly marked
            return false;
          });
          
          return {
            ...link,
            dropdownItems: filteredItems
          };
        }
        return link;
      })
      ?.filter(link => {
        // Remove dropdowns that have no visible items after filtering
        if (link?.hasDropdown && (!link?.dropdownItems || link?.dropdownItems?.length === 0)) {
          return false;
        }
        return true;
      });
  };

  const filteredNavigationLinks = getFilteredNavigationLinks();

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  return (
    <nav ref={navigationRef} className="bg-white dark:bg-gray-800 shadow-lg fixed top-0 left-0 right-0 z-50 transition-colors">
      <div className="w-full px-6 sm:px-8 lg:px-12 py-2 lg:py-4">
        <div className="flex items-center justify-between h-20 gap-8">
          {/* COLUMN 1: Logo, UNESCO Badge, Park Title - Desktop Only */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            {/* Park Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/assets/images/Untitled-1765254849902.jpeg" 
                alt="Circular park logo with white border containing Puerto Princesa Subterranean River National Park text around the perimeter, established 1971 at bottom with yellow stars, center features underground river cave image with tourist boats" 
                className="h-20 w-20 object-contain"
              />
            </Link>

            {/* UNESCO Heritage Site Badge */}
            <div className="flex items-center space-x-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-400 dark:border-yellow-600 rounded-full px-3 py-1.5 flex-shrink-0">
              <Award className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 whitespace-nowrap">
                World Heritage Site
              </span>
            </div>

            {/* Park Title */}
            <h1 className="text-base font-bold text-gray-800 dark:text-gray-200 whitespace-nowrap flex-shrink-0">
              Puerto Princesa Subterranean River National Park
            </h1>
          </div>

          {/* Mobile Header: Logo, Theme Toggle, Menu Button */}
          <div className="flex lg:hidden items-center justify-between w-full gap-2">
            {/* Mobile Logo + Park Title */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Link to="/" className="flex-shrink-0">
                <img 
                  src="/assets/images/Untitled-1765254849902.jpeg" 
                  alt="Circular park logo with white border containing Puerto Princesa Subterranean River National Park text around the perimeter, established 1971 at bottom with yellow stars, center features underground river cave image with tourist boats" 
                  className="h-10 w-10 object-contain"
                />
              </Link>
              
              {/* Park Title - Single Line */}
              <h1 className="text-[10px] sm:text-xs font-bold text-gray-800 dark:text-gray-200 whitespace-nowrap overflow-hidden text-ellipsis">
                Puerto Princesa Subterranean River National Park
              </h1>
            </div>

            {/* Mobile Controls: Theme Toggle + Menu Button */}
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* COLUMN 2: Navigation Menu, Theme Toggle, Auth Buttons - Desktop Only */}
          <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
            {/* Desktop Navigation Menu Items */}
            <div className="flex items-center gap-6" ref={navigationRef}>
              {filteredNavigationLinks?.map((item) => (
                <div key={item?.id || item?.name} className="relative group">
                  {item?.hasDropdown && item?.dropdownItems ? (
                    <>
                      <button
                        onClick={() => toggleDropdown(item?.name)}
                        className="flex items-center space-x-1 text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors font-medium whitespace-nowrap"
                      >
                        <span>{item?.name}</span>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      {openDropdown === item?.name && (
                        <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-xl py-2 transition-colors z-50">
                          {item?.dropdownItems?.map((subItem, index) => (
                            <Link
                              key={subItem?.name || index}
                              to={subItem?.path || '#'}
                              className="block px-4 py-2 text-gray-800 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-600 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {subItem?.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item?.path || '#'}
                      className="text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors font-medium whitespace-nowrap"
                    >
                      {item?.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Theme Toggle */}
            <div className="flex-shrink-0">
              <ThemeToggle />
            </div>

            {/* Auth Buttons */}
            {user ? (
              <div className="flex items-center gap-4 flex-shrink-0">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors font-medium whitespace-nowrap"
                >
                  <User className="h-5 w-5" />
                  <span>Profile</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4 flex-shrink-0">
                <a
                  href="https://app.puertoprincesasubterraneanrivernationalpark.com/login"
                  className="text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors font-medium whitespace-nowrap"
                >
                  Sign In
                </a>
                <a
                  href="https://app.puertoprincesasubterraneanrivernationalpark.com/register"
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium whitespace-nowrap"
                >
                  Sign Up
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden py-4 bg-white dark:bg-gray-800 transition-colors">
          {/* Park Title for Mobile */}
          <div className="px-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-bold text-gray-800 dark:text-gray-200">
              Puerto Princesa Subterranean River National Park
            </h2>
          </div>

          {filteredNavigationLinks?.map((item) => (
            <div key={item?.id || item?.name} className="py-2">
              {item?.hasDropdown && item?.dropdownItems ? (
                <>
                  <button
                    onClick={() => toggleDropdown(item?.name)}
                    className="flex items-center justify-between w-full px-4 py-2 text-gray-800 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  >
                    <span>{item?.name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {openDropdown === item?.name && (
                    <div className="pl-4">
                      {item?.dropdownItems?.map((subItem, index) => (
                        <Link
                          key={subItem?.name || index}
                          to={subItem?.path || '#'}
                          className="block px-4 py-2 text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setOpenDropdown(null);
                          }}
                        >
                          {subItem?.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item?.path || '#'}
                  className="block px-4 py-2 text-gray-800 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item?.name}
                </Link>
              )}
            </div>
          ))}
          
          {/* Mobile Auth */}
          <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4 px-4">
            {user ? (
              <div className="space-y-2">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 py-2 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="h-5 w-5" />
                  <span>Profile</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <a
                  href="https://app.puertoprincesasubterraneanrivernationalpark.com/login"
                  className="block text-center text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 py-2 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </a>
                <a
                  href="https://app.puertoprincesasubterraneanrivernationalpark.com/register"
                  className="block text-center bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}